import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import { ArrowLeft, Mail, Lock, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import productionBackground from "../assets/FlippedKarbala.svg";
import stagingBackground from "../assets/fhimsWallpaper.jpeg";
import logoImage from "../assets/logo.png";
import {
  resetPassword,
  initiateResetPassword,
  resendResetOtp,
} from "../services/authService";

type Step = "email" | "otp" | "success";

interface ForgotPasswordPageProps {
  onBackToLogin: () => void;
}

export const ForgotPasswordPage = ({
  onBackToLogin,
}: ForgotPasswordPageProps) => {
  const [currentStep, setCurrentStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otpInput, setOtpInput] = useState("");

  const OTP_DURATION = 5 * 60; 
  const [otpTimer, setOtpTimer] = useState(0);
  const [isOtpExpired, setIsOtpExpired] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isOtpDisabled = isOtpExpired || isLoading;
  

  const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

  const resetSchema = yup.object().shape({
    otp: yup
      .string()
      .required("OTP is required")
      .length(6, "OTP must be 6 digits"),

    newPassword: yup
      .string()
      .required("New password is required")
      .min(8, "Must be at least 8 characters")
      .matches(/[A-Z]/, "Must contain one uppercase letter")
      .matches(/[a-z]/, "Must contain one lowercase letter")
      .matches(/\d/, "Must contain one number")
      .matches(/[@$!%*?&]/, "Must contain one special character"),

    confirmPassword: yup
      .string()
      .required("Confirm password is required")
      .oneOf([yup.ref("newPassword")], "Passwords must match"),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(resetSchema),
    mode: "onChange",
  });

  const passwordValue = watch("newPassword") || "";

  const isTypingPassword = passwordValue.length > 0;

  const passwordRules = {
    length: passwordValue.length >= 8,
    uppercase: /[A-Z]/.test(passwordValue),
    lowercase: /[a-z]/.test(passwordValue),
    number: /\d/.test(passwordValue),
    special: /[@$!%*?&]/.test(passwordValue),
  };

  const isPasswordValid =
    passwordRules.length &&
    passwordRules.uppercase &&
    passwordRules.lowercase &&
    passwordRules.number &&
    passwordRules.special;

  const backgroundImage =
    process.env.REACT_APP_ENVIRONMENT === "staging"
      ? stagingBackground
      : productionBackground;
  

  useEffect(() => {
    if (currentStep === "otp") {
      setOtpTimer(OTP_DURATION);
      setIsOtpExpired(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    if (currentStep !== "otp") return;

    const interval = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [currentStep]);

 
 useEffect(() => {
    if (isOtpExpired) {
      setOtpInput("");          
      setValue("otp", "");      
      setValue("newPassword", "");
      setValue("confirmPassword", "");
    }
  }, [isOtpExpired, setValue]);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setIsLoading(true);

    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const res = await initiateResetPassword(email);

      toast.success("OTP sent to your registered email");

      setCurrentStep("otp");
    } catch (err: unknown) {
      const errorObj = err as {
        response?: { data?: { message?: string } };
      };

      setError(
        errorObj.response?.data?.message ||
          "Failed to send OTP. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleResetPassword = async (data: any) => {
    setIsLoading(true);

    try {
      const res = await resetPassword({
        email,
        otp: data.otp,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword,
      });

      toast.success(res.message || "Password reset successfully");
      setCurrentStep("success");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const backendMessage =
        err.response?.data?.message || "Something went wrong while resetting.";

      toast.error(backendMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      const res = await resendResetOtp(email);
      toast.success(res.message || "OTP resent successfully");

      setOtpInput("");          
      setValue("otp", ""); 

      setOtpTimer(OTP_DURATION);
      setIsOtpExpired(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to resend OTP");
    }
  };

  const maskEmail = (email: string) => {
    const [name, domain] = email.split("@");

    if (!name) return email;

    const visibleChars = 1;
    const masked =
      name.slice(0, visibleChars) +
      "*".repeat(Math.max(name.length - visibleChars, 0));

    return `${masked}@${domain}`;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "email":
        return (
          <form onSubmit={handleSendOTP} className="space-y-3">
            {error && (
              <Alert
                variant="destructive"
                className="border-red-200 bg-red-50 py-2.5"
              >
                <AlertCircle className="w-4 h-4" />
                <AlertDescription className="text-red-800 text-xs">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-gray-700 text-xs">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your registered email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-10 border-gray-300 rounded-xl bg-white text-sm"
                  required
                />
              </div>
              <p className="text-xs text-gray-600">
                Enter the email address associated with your account
              </p>
            </div>

            <Button
              type="submit"
              className="w-full h-10 text-white rounded-xl shadow-lg flex items-center justify-center text-sm hover:opacity-90 transition-opacity"
              style={{ backgroundColor: "#5B9BD5" }}
              disabled={isLoading}
            >
              {isLoading ? "Sending OTP..." : "Send OTP"}
            </Button>

            <div className="text-center pt-1">
              <button
                type="button"
                onClick={onBackToLogin}
                className="text-sm text-gray-600 hover:text-gray-800 inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </button>
            </div>
          </form>
        );

      case "otp":
        return (
          <form
            onSubmit={handleSubmit(handleResetPassword)}
            className="space-y-4"
          >
            {/* OTP SENT HEADER BOX */}
            <div className="text-center bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Mail className="w-5 h-5 text-blue-600" />
                <h3 className="text-gray-900 text-sm font-semibold">
                  OTP Sent
                </h3>
              </div>
              <p className="text-xs text-gray-600">
                Check your email for the 6-digit OTP
                <br />
              </p>
              <span className="text-blue-600 font-medium">
                {maskEmail(email)}
              </span>
            </div>

            {/* OTP BOXES */}
            <div className="space-y-2">
              <Label className="text-gray-700 text-xs text-center block">
                Enter 6-Digit OTP
              </Label>
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otpInput}
                  disabled={isOtpExpired}
                  onChange={(value) => {
                    setOtpInput(value);
                    setValue("otp", value, { shouldValidate: true });
                  }}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
{/* 
              {errors.otp && (
                <p className="text-red-500 text-xs text-center">
                  {errors.otp.message}
                </p>
              )} */}

              <p className="text-xs sm:text-sm text-center">
                {!isOtpExpired ? (
                  <>
                    OTP expires in{" "}
                    <span
                      className={`font-medium ${
                        otpTimer <= 30 ? "text-red-500" : "text-blue-600"
                      }`}
                    >
                      {formatTime(otpTimer)}
                    </span>
                  </>
                ) : (
                  <span className="text-gray-500 font-medium">
                    OTP has expired
                  </span>
                )}
              </p>
            </div>

            {/* NEW PASSWORD */}
            <div className="space-y-1.5">
              <Label className="text-gray-700 text-xs">New Password</Label>

              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="password"
                  placeholder="Enter new password"
                  {...register("newPassword")}
                  className={`pl-10 h-10 rounded-xl ${
                    errors.newPassword ? "border-red-500" : ""
                  }`}
                />
              </div>

              {/* PASSWORD CRITERIA */}
              {isTypingPassword && !isPasswordValid && (
                <div className="text-xs space-y-1 mt-2">
                  <p
                    className={
                      passwordRules.length ? "text-green-600" : "text-gray-500"
                    }
                  >
                    • At least 8 characters
                  </p>
                  <p
                    className={
                      passwordRules.uppercase
                        ? "text-green-600"
                        : "text-gray-500"
                    }
                  >
                    • One uppercase letter
                  </p>
                  <p
                    className={
                      passwordRules.lowercase
                        ? "text-green-600"
                        : "text-gray-500"
                    }
                  >
                    • One lowercase letter
                  </p>
                  <p
                    className={
                      passwordRules.number ? "text-green-600" : "text-gray-500"
                    }
                  >
                    • One number
                  </p>
                  <p
                    className={
                      passwordRules.special ? "text-green-600" : "text-gray-500"
                    }
                  >
                    • One special character (@$!%*?&)
                  </p>
                </div>
              )}

              {errors.newPassword && (
                <p className="text-red-500 text-xs">
                  {errors.newPassword.message}
                </p>
              )}
            </div>

            {/* CONFIRM PASSWORD */}
            <div className="space-y-1.5">
              <Label className="text-gray-700 text-xs">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  {...register("confirmPassword")}
                  className={`pl-10 h-10 rounded-xl ${
                    errors.confirmPassword ? "border-red-500" : ""
                  }`}
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-xs">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            {/* RESEND */}
            <div className="text-right">
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={!isOtpExpired}
                className={`text-sm transition-colors ${
                  isOtpExpired
                    ? "text-blue-600 hover:text-blue-700"
                    : "text-gray-400 cursor-not-allowed"
                }`}
              >
                Didn’t receive OTP? Resend
              </button>
            </div>

            {/* SUBMIT */}
            <Button
              type="submit"
              disabled={
                isLoading ||
                isOtpExpired ||
                !isPasswordValid ||
                watch("otp")?.length !== 6 ||
                watch("confirmPassword") !== watch("newPassword")
              }
              className="w-full h-10 text-white rounded-xl shadow-lg text-sm"
              style={{ backgroundColor: "#5B9BD5" }}
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </Button>

            {/* BACK */}
            <div className="text-center">
              <button
                type="button"
                onClick={onBackToLogin}
                className="text-sm text-gray-600 hover:text-gray-800 inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </button>
            </div>
          </form>
        );

      case "success":
        return (
          <div className="text-center space-y-3">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <div className="space-y-1">
              <h2 className="text-gray-900 text-xl">
                Password Reset Successful!
              </h2>
              <p className="text-gray-600 text-sm">
                Your password has been updated successfully.
                <br />
                You can now login with your new credentials.
              </p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-3">
              <p className="text-sm text-green-800">
                Your account is now secure with the new password.
              </p>
            </div>
            <Button
              onClick={onBackToLogin}
              className="w-full h-10 text-white rounded-xl shadow-lg flex items-center justify-center text-sm hover:opacity-90 transition-opacity"
              style={{ backgroundColor: "#5B9BD5" }}
            >
              Continue to Login
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="h-screen w-full relative overflow-hidden">
      {/* Full Screen Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src={backgroundImage}
          alt="Mosque Background"
          className="w-full h-full object-cover"
        />
        {/* Overlay for visibility */}
        <div className="absolute inset-0 bg-black/30"></div>
      </div>

      {/* Content Container */}
      <div className="relative h-full flex items-center justify-center p-6">
        {/* Forgot Password Card - Centered */}
        <div className="w-full max-w-md">
          {/* Logo Above Card */}
          <div className="flex justify-center mb-4">
            <img
              src={logoImage}
              alt="Faiz-e-Husayni Society Logo"
              className="w-32 h-32 object-contain drop-shadow-2xl"
            />
          </div>

          {/* Heading */}
          <div className="text-center mb-5">
            <h1 className="text-white text-2xl mb-1.5 drop-shadow-lg">
              {currentStep === "email" && "Forgot Password?"}
              {currentStep === "otp" && "Verify OTP"}
              {currentStep === "success" && "All Done!"}
            </h1>
            <p className="text-white/90 text-sm drop-shadow-lg">
              {currentStep === "email" && "Reset your account password"}
              {currentStep === "otp" && "Enter the code sent to your email"}
              {currentStep === "success" && "Your password has been reset"}
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <div
              className={`flex-1 h-1 rounded-full transition-all ${
                currentStep === "email" ? "bg-white" : "bg-white/30"
              }`}
            />
            <div
              className={`flex-1 h-1 rounded-full transition-all ${
                currentStep === "otp" || currentStep === "success"
                  ? "bg-white"
                  : "bg-white/30"
              }`}
            />
            {/* <div
                  className={`flex-1 h-1 rounded-full transition-all ${
                      currentStep === "password" || currentStep === "success"
                          ? "bg-white"
                          : "bg-white/30"
                  }`}
              /> */}
            <div
              className={`flex-1 h-1 rounded-full transition-all ${
                currentStep === "success" ? "bg-green-400" : "bg-white/30"
              }`}
            />
          </div>

          {/* Card Content */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-6">
            {renderStepContent()}
          </div>

          {/* Footer */}
          <div className="text-center mt-3 text-white/90 text-[10px] drop-shadow-lg">
            <p>© 2025 Faiz e Husaini Information Management System</p>
          </div>
        </div>
      </div>
    </div>
  );
};
